import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  X, Megaphone, Sparkles, Calendar, MessageCircle, Tag, Wallet, TrendingUp,
  Activity, Target, AlertTriangle, CheckCircle2, Copy, RefreshCw, MapPin,
  Zap, Flame, ChevronRight, Loader2, Globe, DollarSign, Clock, Lightbulb, Languages
} from 'lucide-react';

type Language = 'English' | 'Sinhala' | 'Tamil' | 'Arabic';
const LANGUAGES: { id: Language; label: string; native: string; dir: 'ltr' | 'rtl'; font: string }[] = [
  { id: 'English', label: 'English', native: 'English',  dir: 'ltr', font: "'Inter', system-ui, sans-serif" },
  { id: 'Sinhala', label: 'Sinhala', native: 'සිංහල',   dir: 'ltr', font: "'Noto Sans Sinhala', 'Iskoola Pota', sans-serif" },
  { id: 'Tamil',   label: 'Tamil',   native: 'தமிழ்',     dir: 'ltr', font: "'Noto Sans Tamil', 'Latha', sans-serif" },
  { id: 'Arabic',  label: 'Arabic',  native: 'عربي',     dir: 'rtl', font: "'Noto Sans Arabic', 'Tahoma', sans-serif" },
];

interface MarketingPlan {
  businessPulse: {
    strengths: string[];
    weaknesses: string[];
    opportunities: string[];
    threats: string[];
  };
  weeklyActions: Array<{ priority: string; title: string; why: string; how: string; channel: string }>;
  festivalCalendar: Array<{ name: string; date: string; daysAway: number; campaignIdea: string; suggestedBudget: number }>;
  contentStudio: {
    whatsappBroadcast: { sinhala: string; english: string; imagePrompt: string };
    facebookPost: { caption: string; hashtags: string[]; imagePrompt: string };
    tiktokReel: { hook: string; script: string; soundIdea: string };
  };
  smartOffers: Array<{ combo: string; bundlePrice: number; rationale: string; expectedLift: string }>;
  budgetAllocation: {
    tier: string;
    monthly: number;
    breakdown: Array<{ channel: string; amount: number; percentage: number; expectedROAS: number }>;
  };
  forecast: {
    stockoutAlerts: Array<{ product: string; daysToStockout: number; action: string }>;
    revenueProjection: { nextWeek: number; nextMonth: number; confidence: string };
    insights: string[];
  };
  kpis: Array<{ metric: string; current: number; target: number; timeframe: string }>;
}

interface StoreProfile {
  address: string;
  audience: string;
  budgetTier: 'small' | 'medium' | 'large';
  hasDelivery: boolean;
  hasWhatsApp: boolean;
  goals: string;
}

interface LunaMarketingBrainProps {
  isOpen: boolean;
  onClose: () => void;
  storeApiKey: string;
  storeName: string;
  analytics: any; // Full analytics object from POSDashboard
}

type Section = 'pulse' | 'actions' | 'festivals' | 'content' | 'offers' | 'budget' | 'forecast' | 'kpis';

const SECTIONS: { id: Section; label: string; icon: any; color: string }[] = [
  { id: 'pulse',     label: 'Business Pulse',    icon: Activity,      color: 'text-cyan-300' },
  { id: 'actions',   label: 'Weekly Actions',    icon: Zap,           color: 'text-amber-300' },
  { id: 'festivals', label: 'Festival Calendar', icon: Calendar,      color: 'text-rose-300' },
  { id: 'content',   label: 'Content Studio',    icon: MessageCircle, color: 'text-violet-300' },
  { id: 'offers',    label: 'Smart Offers',      icon: Tag,           color: 'text-fuchsia-300' },
  { id: 'budget',    label: 'Budget Optimizer',  icon: Wallet,        color: 'text-emerald-300' },
  { id: 'forecast',  label: 'Forecast & Alerts', icon: TrendingUp,    color: 'text-orange-300' },
  { id: 'kpis',      label: 'KPIs',              icon: Target,        color: 'text-blue-300' },
];

const formatLKR = (n: number) => 'Rs. ' + (n || 0).toLocaleString('en-LK', { maximumFractionDigits: 0 });

const PROFILE_KEY_PREFIX = 'luna_marketing_profile_';
// LOW-2: Derive a safe localStorage key from storeName (never embed raw API key in key names)
const safeStoreKey = (name: string) => name.replace(/[^a-zA-Z0-9]/g, '_').slice(0, 32);

export default function LunaMarketingBrain({ isOpen, onClose, storeApiKey, storeName, analytics }: LunaMarketingBrainProps) {
  const profileKey = PROFILE_KEY_PREFIX + safeStoreKey(storeName);
  const [section, setSection] = useState<Section>('pulse');
  const [plan, setPlan] = useState<MarketingPlan | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [generatedAt, setGeneratedAt] = useState<string>('');
  const [showProfile, setShowProfile] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [preferredLanguage, setPreferredLanguage] = useState<Language>(() => {
    return (localStorage.getItem('luna_marketing_lang_' + safeStoreKey(storeName)) as Language) || 'English';
  });
  const langMeta = LANGUAGES.find(l => l.id === preferredLanguage) || LANGUAGES[0];

  useEffect(() => {
    localStorage.setItem('luna_marketing_lang_' + safeStoreKey(storeName), preferredLanguage);
  }, [preferredLanguage, storeApiKey]);

  const [profile, setProfile] = useState<StoreProfile>(() => {
    try {
      const raw = localStorage.getItem(profileKey);
      return raw ? JSON.parse(raw) : {
        address: '', audience: 'Mixed — housewives, office commuters, bachelors',
        budgetTier: 'medium', hasDelivery: false, hasWhatsApp: true,
        goals: 'Increase footfall and build a loyal WhatsApp community',
      };
    } catch {
      return { address: '', audience: '', budgetTier: 'medium', hasDelivery: false, hasWhatsApp: true, goals: '' };
    }
  });

  useEffect(() => {
    if (isOpen) {
      // Cache the last plan per store
      try {
        const cachedRaw = localStorage.getItem('luna_marketing_plan_' + safeStoreKey(storeName));
        if (cachedRaw) {
          const cached = JSON.parse(cachedRaw);
          setPlan(cached.plan);
          setGeneratedAt(cached.generatedAt);
        }
      } catch { /* ignore */ }
    }
  }, [isOpen, storeApiKey]);

  const saveProfile = () => {
    localStorage.setItem(profileKey, JSON.stringify(profile));
    setShowProfile(false);
  };

  const generatePlan = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/luna/marketing/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeApiKey, analytics, profile, language: preferredLanguage }),
      });
      const data = await res.json();
      if (!res.ok) {
        // Backend returns { error, status, details, hint }
        const parts: string[] = [];
        if (data.error) parts.push(data.error);
        if (data.status) parts.push(`[${data.status}]`);
        if (data.details && data.details !== data.error) parts.push('— ' + data.details);
        if (data.hint) parts.push('\n💡 ' + data.hint);
        throw new Error(parts.join(' ') || 'Failed');
      }
      setPlan(data.plan);
      setGeneratedAt(data.generatedAt);
      localStorage.setItem('luna_marketing_plan_' + safeStoreKey(storeName), JSON.stringify({ plan: data.plan, generatedAt: data.generatedAt }));
    } catch (e: any) {
      setError(e.message || 'Luna could not generate the plan right now');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(null), 1800);
    } catch { /* ignore */ }
  };

  const priorityStyle: Record<string, string> = {
    high:   'bg-red-500/10 border-red-500/30 text-red-300',
    medium: 'bg-amber-500/10 border-amber-500/30 text-amber-300',
    low:    'bg-emerald-500/10 border-emerald-500/30 text-emerald-300',
  };

  const confidenceStyle: Record<string, string> = {
    low:    'text-red-300 bg-red-500/10 border-red-500/30',
    medium: 'text-amber-300 bg-amber-500/10 border-amber-500/30',
    high:   'text-emerald-300 bg-emerald-500/10 border-emerald-500/30',
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9996] bg-black/85 backdrop-blur-2xl flex items-center justify-center p-4"
          onClick={e => { if (e.target === e.currentTarget) onClose(); }}
        >
          <motion.div
            initial={{ scale: 0.96, y: 20, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.96, y: 20, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="relative w-full max-w-7xl h-[92vh] rounded-3xl bg-[#07061a] border border-fuchsia-500/25 shadow-[0_0_80px_rgba(217,70,239,0.22)] flex overflow-hidden"
          >
            {/* Ambient glows */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
              <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-fuchsia-500/8 blur-[160px]" />
              <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full bg-violet-500/8 blur-[160px]" />
            </div>

            {/* ── SIDEBAR ─────────────────────────────────────── */}
            <aside className="relative z-10 w-64 shrink-0 bg-black/40 border-r border-white/[0.06] flex flex-col">
              <div className="px-5 py-5 border-b border-white/[0.06]">
                <div className="flex items-center gap-2.5 mb-1">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-fuchsia-500/20 to-violet-500/20 border border-fuchsia-500/30 flex items-center justify-center">
                    <Megaphone size={17} className="text-fuchsia-300" />
                  </div>
                  <div>
                    <div className="text-[13px] font-black text-white leading-tight">Marketing Brain</div>
                    <div className="text-[9px] font-mono text-slate-600 uppercase tracking-widest">Luna · SL</div>
                  </div>
                </div>
                <div className="text-[10px] text-slate-500 mt-2 truncate">{storeName}</div>
              </div>

              <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5">
                {SECTIONS.map(s => {
                  const Icon = s.icon;
                  const active = section === s.id;
                  return (
                    <button key={s.id} onClick={() => setSection(s.id)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-[12px] font-semibold text-left transition-all ${
                        active ? 'bg-gradient-to-r from-fuchsia-500/15 to-violet-500/10 border border-fuchsia-500/25 text-white shadow-[0_0_18px_rgba(217,70,239,0.12)]'
                               : 'text-slate-500 hover:text-white hover:bg-white/[0.03] border border-transparent'
                      }`}>
                      <Icon size={13} className={active ? s.color : 'text-slate-600'} />
                      <span className="flex-1">{s.label}</span>
                      {active && <ChevronRight size={12} className="text-fuchsia-400" />}
                    </button>
                  );
                })}
              </nav>

              <div className="p-3 border-t border-white/[0.06] space-y-2">
                {/* Language selector */}
                <div>
                  <label className="flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-widest text-slate-600 mb-1.5">
                    <Languages size={10} /> Output Language
                  </label>
                  <div className="grid grid-cols-2 gap-1.5">
                    {LANGUAGES.map(l => {
                      const active = preferredLanguage === l.id;
                      return (
                        <button key={l.id} onClick={() => setPreferredLanguage(l.id)}
                          dir={l.dir}
                          style={{ fontFamily: l.font }}
                          className={`py-1.5 px-2 rounded-lg border text-[10px] font-bold transition-all ${
                            active ? 'bg-fuchsia-500/15 border-fuchsia-500/40 text-fuchsia-100'
                                   : 'bg-white/[0.02] border-white/[0.06] text-slate-500 hover:text-white hover:border-white/[0.12]'
                          }`}>
                          {l.native}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <button onClick={() => setShowProfile(v => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.02] border border-white/[0.06] hover:border-violet-500/30 text-[10px] font-bold text-slate-400 hover:text-violet-300 transition-all">
                  <MapPin size={11} /> Store Profile
                </button>
                <button onClick={generatePlan} disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl font-black text-[10.5px] uppercase tracking-widest bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white shadow-[0_0_20px_rgba(217,70,239,0.35)] hover:shadow-[0_0_32px_rgba(217,70,239,0.5)] disabled:opacity-50 disabled:cursor-not-allowed transition-all">
                  {loading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                  {loading ? 'Thinking…' : plan ? 'Regenerate' : 'Generate Plan'}
                </button>
                {generatedAt && (
                  <div className="text-[8px] font-mono text-slate-700 text-center">
                    Last: {new Date(generatedAt).toLocaleString('en-LK', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </div>
                )}
              </div>
            </aside>

            {/* ── MAIN ────────────────────────────────────────── */}
            <div className="relative z-10 flex-1 flex flex-col overflow-hidden">
              <header className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06] shrink-0">
                <div className="flex items-center gap-2.5">
                  {(() => {
                    const curr = SECTIONS.find(s => s.id === section)!;
                    const Icon = curr.icon;
                    return <>
                      <div className="w-9 h-9 rounded-xl bg-white/[0.03] border border-white/[0.08] flex items-center justify-center">
                        <Icon size={16} className={curr.color} />
                      </div>
                      <div>
                        <h2 className="text-[14px] font-black text-white">{curr.label}</h2>
                        <p className="text-[10px] text-slate-600 font-mono uppercase tracking-[0.2em]">Sri Lanka · Local marketing intelligence</p>
                      </div>
                    </>;
                  })()}
                </div>
                <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-white/5 flex items-center justify-center text-slate-500 hover:text-white transition-colors">
                  <X size={16} />
                </button>
              </header>

              <div className="flex-1 overflow-y-auto p-6" dir={langMeta.dir} style={{ fontFamily: preferredLanguage === 'English' ? undefined : langMeta.font }}>
                {/* Error */}
                {error && (
                  <div className="mb-4 flex items-start gap-2 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/25 text-red-300 text-[12px] whitespace-pre-wrap leading-relaxed">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" /> <span className="flex-1 break-words">{error}</span>
                  </div>
                )}

                {/* Empty state */}
                {!plan && !loading && (
                  <div className="flex flex-col items-center justify-center py-20 text-center">
                    <motion.div animate={{ rotate: [0, 8, -8, 0] }} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}>
                      <Megaphone size={56} className="text-fuchsia-400/70 mb-4" />
                    </motion.div>
                    <h3 className="text-[18px] font-black text-white mb-2">Luna is ready to build your marketing plan</h3>
                    <p className="text-[12px] text-slate-500 max-w-md mx-auto mb-5">
                      Based on your POS data, inventory, peak hours and Sri Lankan local market dynamics — Luna will generate a complete digital strategy tailored to <span className="text-white font-bold">{storeName}</span>.
                    </p>
                    <button onClick={generatePlan} className="flex items-center gap-2 px-6 py-3 rounded-2xl font-black text-[12px] uppercase tracking-widest bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white shadow-[0_0_28px_rgba(217,70,239,0.4)]">
                      <Sparkles size={14} /> Generate Marketing Plan
                    </button>
                  </div>
                )}

                {/* Loading */}
                {loading && !plan && (
                  <div className="flex flex-col items-center justify-center py-24">
                    <Loader2 size={40} className="text-fuchsia-400 animate-spin mb-3" />
                    <p className="text-[13px] text-white font-bold">Luna is analysing your store…</p>
                    <p className="text-[11px] text-slate-500 mt-1 font-mono">Reading POS data · Checking SL market · Building plan</p>
                  </div>
                )}

                {/* ─── BUSINESS PULSE ─── */}
                {plan && section === 'pulse' && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {[
                      { title: 'Strengths',     items: plan.businessPulse.strengths,     color: 'emerald', icon: CheckCircle2 },
                      { title: 'Weaknesses',    items: plan.businessPulse.weaknesses,    color: 'red',     icon: AlertTriangle },
                      { title: 'Opportunities', items: plan.businessPulse.opportunities, color: 'cyan',    icon: Sparkles },
                      { title: 'Threats',       items: plan.businessPulse.threats,       color: 'amber',   icon: Flame },
                    ].map(block => {
                      const tintMap: Record<string, string> = {
                        emerald: 'from-emerald-500/10 to-emerald-500/0 border-emerald-500/25 text-emerald-300',
                        red:     'from-red-500/10 to-red-500/0 border-red-500/25 text-red-300',
                        cyan:    'from-cyan-500/10 to-cyan-500/0 border-cyan-500/25 text-cyan-300',
                        amber:   'from-amber-500/10 to-amber-500/0 border-amber-500/25 text-amber-300',
                      };
                      const Icon = block.icon;
                      return (
                        <div key={block.title} className={`rounded-2xl border p-5 bg-gradient-to-br ${tintMap[block.color]}`}>
                          <div className="flex items-center gap-2 mb-4">
                            <Icon size={14} /> <span className="text-[10px] font-black uppercase tracking-[0.2em]">{block.title}</span>
                          </div>
                          <ul className="space-y-2.5">
                            {block.items.map((item, i) => (
                              <li key={i} className="flex items-start gap-2 text-[12px] text-slate-200 leading-relaxed">
                                <span className="text-[10px] mt-1">●</span>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* ─── WEEKLY ACTIONS ─── */}
                {plan && section === 'actions' && (
                  <div className="space-y-3">
                    {plan.weeklyActions.map((a, i) => (
                      <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                        className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 hover:border-amber-500/25 transition-all">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded-md border text-[9px] font-black uppercase tracking-wider ${priorityStyle[a.priority] || priorityStyle.medium}`}>
                              {a.priority}
                            </span>
                            <span className="px-2 py-0.5 rounded-md bg-violet-500/10 border border-violet-500/20 text-violet-300 text-[9px] font-bold uppercase tracking-wider">
                              {a.channel}
                            </span>
                          </div>
                        </div>
                        <h4 className="text-[14px] font-black text-white mb-1.5">{a.title}</h4>
                        <p className="text-[11px] text-slate-400 mb-2"><span className="text-amber-300 font-bold">Why: </span>{a.why}</p>
                        <p className="text-[11px] text-slate-300 leading-relaxed"><span className="text-emerald-300 font-bold">How: </span>{a.how}</p>
                      </motion.div>
                    ))}
                  </div>
                )}

                {/* ─── FESTIVALS ─── */}
                {plan && section === 'festivals' && (
                  <div className="space-y-3">
                    {plan.festivalCalendar.map((f, i) => (
                      <div key={i} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 flex items-center gap-4">
                        <div className="shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-rose-500/15 to-pink-500/15 border border-rose-500/25 flex flex-col items-center justify-center">
                          <div className="text-[20px] font-black text-white leading-none">{f.daysAway}</div>
                          <div className="text-[8px] font-mono text-rose-300 uppercase tracking-wider mt-0.5">days</div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="text-[13px] font-black text-white">{f.name}</h4>
                            <span className="text-[10px] font-mono text-slate-600">{f.date}</span>
                          </div>
                          <p className="text-[11px] text-slate-400 leading-relaxed">{f.campaignIdea}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-[10px] font-mono text-slate-600 uppercase tracking-wider">Budget</div>
                          <div className="text-[13px] font-black text-emerald-300 font-mono">{formatLKR(f.suggestedBudget)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* ─── CONTENT STUDIO ─── */}
                {plan && section === 'content' && (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* WhatsApp */}
                    <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/5 p-5 flex flex-col">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-7 h-7 rounded-lg bg-emerald-500/15 flex items-center justify-center"><MessageCircle size={13} className="text-emerald-300" /></div>
                        <span className="text-[11px] font-black text-emerald-300 uppercase tracking-wider">WhatsApp Broadcast</span>
                      </div>
                      <div className="space-y-3 text-[12px] flex-1">
                        <div>
                          <div className="text-[9px] font-mono text-slate-600 uppercase tracking-widest mb-1">Sinhala</div>
                          <p className="text-slate-200 leading-relaxed whitespace-pre-wrap">{plan.contentStudio.whatsappBroadcast.sinhala}</p>
                        </div>
                        <div>
                          <div className="text-[9px] font-mono text-slate-600 uppercase tracking-widest mb-1">English</div>
                          <p className="text-slate-200 leading-relaxed whitespace-pre-wrap">{plan.contentStudio.whatsappBroadcast.english}</p>
                        </div>
                        <div className="pt-2 border-t border-white/[0.05]">
                          <div className="text-[9px] font-mono text-slate-600 uppercase tracking-widest mb-1">🎨 Image Prompt</div>
                          <p className="text-[10px] text-slate-500 italic">{plan.contentStudio.whatsappBroadcast.imagePrompt}</p>
                        </div>
                      </div>
                      <button onClick={() => copyToClipboard(`${plan.contentStudio.whatsappBroadcast.sinhala}\n\n${plan.contentStudio.whatsappBroadcast.english}`, 'wa')}
                        className="mt-3 flex items-center justify-center gap-1.5 w-full py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 text-[10px] font-bold uppercase tracking-wider hover:bg-emerald-500/20 transition-all">
                        {copied === 'wa' ? <><CheckCircle2 size={11}/> Copied</> : <><Copy size={11}/> Copy</>}
                      </button>
                    </div>

                    {/* Facebook */}
                    <div className="rounded-2xl border border-blue-500/25 bg-blue-500/5 p-5 flex flex-col">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-7 h-7 rounded-lg bg-blue-500/15 flex items-center justify-center"><Globe size={13} className="text-blue-300" /></div>
                        <span className="text-[11px] font-black text-blue-300 uppercase tracking-wider">Facebook Post</span>
                      </div>
                      <div className="space-y-3 text-[12px] flex-1">
                        <p className="text-slate-200 leading-relaxed whitespace-pre-wrap">{plan.contentStudio.facebookPost.caption}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {plan.contentStudio.facebookPost.hashtags.map((h, i) => (
                            <span key={i} className="px-2 py-0.5 rounded-md bg-blue-500/10 border border-blue-500/20 text-blue-300 text-[10px] font-mono">{h.startsWith('#') ? h : `#${h}`}</span>
                          ))}
                        </div>
                        <div className="pt-2 border-t border-white/[0.05]">
                          <div className="text-[9px] font-mono text-slate-600 uppercase tracking-widest mb-1">🎨 Image Prompt</div>
                          <p className="text-[10px] text-slate-500 italic">{plan.contentStudio.facebookPost.imagePrompt}</p>
                        </div>
                      </div>
                      <button onClick={() => copyToClipboard(`${plan.contentStudio.facebookPost.caption}\n\n${plan.contentStudio.facebookPost.hashtags.map(h=>h.startsWith('#')?h:`#${h}`).join(' ')}`, 'fb')}
                        className="mt-3 flex items-center justify-center gap-1.5 w-full py-2 rounded-lg bg-blue-500/10 border border-blue-500/25 text-blue-300 text-[10px] font-bold uppercase tracking-wider hover:bg-blue-500/20 transition-all">
                        {copied === 'fb' ? <><CheckCircle2 size={11}/> Copied</> : <><Copy size={11}/> Copy</>}
                      </button>
                    </div>

                    {/* TikTok */}
                    <div className="rounded-2xl border border-pink-500/25 bg-pink-500/5 p-5 flex flex-col">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-7 h-7 rounded-lg bg-pink-500/15 flex items-center justify-center"><Sparkles size={13} className="text-pink-300" /></div>
                        <span className="text-[11px] font-black text-pink-300 uppercase tracking-wider">TikTok Reel</span>
                      </div>
                      <div className="space-y-3 text-[12px] flex-1">
                        <div>
                          <div className="text-[9px] font-mono text-slate-600 uppercase tracking-widest mb-1">Hook (0-3s)</div>
                          <p className="text-white font-bold leading-relaxed">{plan.contentStudio.tiktokReel.hook}</p>
                        </div>
                        <div>
                          <div className="text-[9px] font-mono text-slate-600 uppercase tracking-widest mb-1">Script</div>
                          <p className="text-slate-200 leading-relaxed whitespace-pre-wrap">{plan.contentStudio.tiktokReel.script}</p>
                        </div>
                        <div className="pt-2 border-t border-white/[0.05]">
                          <div className="text-[9px] font-mono text-slate-600 uppercase tracking-widest mb-1">🎵 Sound Idea</div>
                          <p className="text-[10px] text-slate-400 italic">{plan.contentStudio.tiktokReel.soundIdea}</p>
                        </div>
                      </div>
                      <button onClick={() => copyToClipboard(`HOOK: ${plan.contentStudio.tiktokReel.hook}\n\nSCRIPT: ${plan.contentStudio.tiktokReel.script}\n\nSOUND: ${plan.contentStudio.tiktokReel.soundIdea}`, 'tt')}
                        className="mt-3 flex items-center justify-center gap-1.5 w-full py-2 rounded-lg bg-pink-500/10 border border-pink-500/25 text-pink-300 text-[10px] font-bold uppercase tracking-wider hover:bg-pink-500/20 transition-all">
                        {copied === 'tt' ? <><CheckCircle2 size={11}/> Copied</> : <><Copy size={11}/> Copy</>}
                      </button>
                    </div>
                  </div>
                )}

                {/* ─── SMART OFFERS ─── */}
                {plan && section === 'offers' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {plan.smartOffers.map((o, i) => (
                      <div key={i} className="rounded-2xl border border-fuchsia-500/20 bg-gradient-to-br from-fuchsia-500/5 to-violet-500/5 p-5">
                        <div className="flex items-center gap-2 mb-3">
                          <Tag size={13} className="text-fuchsia-300" />
                          <span className="text-[10px] font-black uppercase tracking-widest text-fuchsia-300">Combo Offer #{i+1}</span>
                        </div>
                        <h4 className="text-[14px] font-black text-white mb-2">{o.combo}</h4>
                        <div className="text-[22px] font-black font-mono bg-clip-text text-transparent bg-gradient-to-r from-emerald-400 to-cyan-400 mb-3 leading-none">{formatLKR(o.bundlePrice)}</div>
                        <p className="text-[11px] text-slate-400 leading-relaxed mb-2"><span className="text-fuchsia-300 font-bold">Why: </span>{o.rationale}</p>
                        <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-[10px] font-bold">
                          <TrendingUp size={10} /> {o.expectedLift}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* ─── BUDGET ─── */}
                {plan && section === 'budget' && (
                  <div>
                    <div className="flex items-center justify-between mb-5 rounded-2xl border border-emerald-500/25 bg-gradient-to-br from-emerald-500/10 to-emerald-500/0 p-5">
                      <div>
                        <div className="text-[10px] font-mono text-emerald-300 uppercase tracking-[0.2em] mb-1">Recommended Monthly Budget · {plan.budgetAllocation.tier}</div>
                        <div className="text-[28px] font-black text-white font-mono leading-none">{formatLKR(plan.budgetAllocation.monthly)}</div>
                      </div>
                      <DollarSign size={32} className="text-emerald-400/50" />
                    </div>
                    <div className="space-y-2.5">
                      {plan.budgetAllocation.breakdown.map((b, i) => (
                        <div key={i} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-[13px] font-bold text-white">{b.channel}</span>
                              <span className="text-[10px] font-mono text-slate-500">{b.percentage}%</span>
                            </div>
                            <div className="flex items-center gap-4">
                              <span className="text-[10px] font-mono text-cyan-300">ROAS {b.expectedROAS}x</span>
                              <span className="text-[13px] font-black text-white font-mono">{formatLKR(b.amount)}</span>
                            </div>
                          </div>
                          <div className="h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
                            <motion.div initial={{ width: 0 }} animate={{ width: `${b.percentage}%` }} transition={{ duration: 0.6, delay: i * 0.05 }}
                              className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-cyan-400" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ─── FORECAST ─── */}
                {plan && section === 'forecast' && (
                  <div className="space-y-4">
                    {/* Projection */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
                        <div className="text-[9px] font-mono text-slate-500 uppercase tracking-[0.2em] mb-1.5 flex items-center gap-1"><Clock size={10}/> Next Week</div>
                        <div className="text-[20px] font-black text-white font-mono">{formatLKR(plan.forecast.revenueProjection.nextWeek)}</div>
                      </div>
                      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
                        <div className="text-[9px] font-mono text-slate-500 uppercase tracking-[0.2em] mb-1.5 flex items-center gap-1"><Calendar size={10}/> Next Month</div>
                        <div className="text-[20px] font-black text-white font-mono">{formatLKR(plan.forecast.revenueProjection.nextMonth)}</div>
                      </div>
                      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
                        <div className="text-[9px] font-mono text-slate-500 uppercase tracking-[0.2em] mb-1.5 flex items-center gap-1"><Target size={10}/> Confidence</div>
                        <span className={`inline-block px-2 py-0.5 rounded-md border text-[11px] font-black uppercase tracking-wider ${confidenceStyle[plan.forecast.revenueProjection.confidence] || confidenceStyle.medium}`}>
                          {plan.forecast.revenueProjection.confidence}
                        </span>
                      </div>
                    </div>

                    {/* Stockout alerts */}
                    {plan.forecast.stockoutAlerts.length > 0 && (
                      <div className="rounded-2xl border border-red-500/25 bg-red-500/5 p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <AlertTriangle size={13} className="text-red-400" />
                          <span className="text-[11px] font-black uppercase tracking-wider text-red-300">Stockout Alerts</span>
                        </div>
                        <div className="space-y-2">
                          {plan.forecast.stockoutAlerts.map((s, i) => (
                            <div key={i} className="flex items-center gap-3 text-[11px]">
                              <div className="w-14 text-center px-2 py-1 rounded-lg bg-red-500/15 border border-red-500/25 text-red-300 font-black font-mono">{s.daysToStockout}d</div>
                              <div className="flex-1">
                                <div className="font-bold text-white">{s.product}</div>
                                <div className="text-slate-400 text-[10px]">{s.action}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Insights */}
                    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Lightbulb size={13} className="text-amber-300" />
                        <span className="text-[11px] font-black uppercase tracking-wider text-amber-300">Luna's Insights</span>
                      </div>
                      <ul className="space-y-2">
                        {plan.forecast.insights.map((ins, i) => (
                          <li key={i} className="flex items-start gap-2 text-[12px] text-slate-300 leading-relaxed">
                            <Sparkles size={10} className="text-amber-400 mt-1 shrink-0" /> {ins}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* ─── KPIs ─── */}
                {plan && section === 'kpis' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {plan.kpis.map((k, i) => {
                      const pct = k.target ? Math.min(100, (k.current / k.target) * 100) : 0;
                      return (
                        <div key={i} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[12px] font-bold text-white">{k.metric}</span>
                            <span className="text-[9px] font-mono text-slate-500 uppercase tracking-wider">{k.timeframe}</span>
                          </div>
                          <div className="flex items-end gap-2 mb-2">
                            <span className="text-[18px] font-black text-white font-mono leading-none">{k.current}</span>
                            <span className="text-[11px] text-slate-600 font-mono">/ {k.target}</span>
                          </div>
                          <div className="h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
                            <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.6, delay: i * 0.04 }}
                              className="h-full rounded-full bg-gradient-to-r from-blue-400 to-violet-400" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* ── STORE PROFILE DRAWER ─── */}
            <AnimatePresence>
              {showProfile && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="absolute inset-0 z-20 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6"
                  onClick={e => { if (e.target === e.currentTarget) setShowProfile(false); }}
                >
                  <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
                    className="w-full max-w-md bg-[#0a0a15] border border-fuchsia-500/25 rounded-3xl p-5 space-y-3 shadow-[0_0_40px_rgba(217,70,239,0.25)]"
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="text-[14px] font-black text-white flex items-center gap-2"><MapPin size={14}/> Store Profile</h3>
                      <button onClick={() => setShowProfile(false)} className="text-slate-500 hover:text-white"><X size={14}/></button>
                    </div>
                    <p className="text-[10px] text-slate-500">Luna uses this context to tune the marketing plan for your specific store reality.</p>

                    <label className="block">
                      <span className="text-[9px] font-mono uppercase tracking-widest text-slate-600">Address / Area</span>
                      <input value={profile.address} onChange={e => setProfile(p => ({...p, address: e.target.value}))}
                        placeholder="e.g. Maharagama, 2km radius"
                        className="w-full mt-1 bg-white/[0.03] border border-white/[0.08] rounded-xl px-3 py-2 text-[12px] text-white outline-none focus:border-fuchsia-500/40" />
                    </label>

                    <label className="block">
                      <span className="text-[9px] font-mono uppercase tracking-widest text-slate-600">Target Audience</span>
                      <input value={profile.audience} onChange={e => setProfile(p => ({...p, audience: e.target.value}))}
                        placeholder="e.g. Housewives, office workers, mechanics"
                        className="w-full mt-1 bg-white/[0.03] border border-white/[0.08] rounded-xl px-3 py-2 text-[12px] text-white outline-none focus:border-fuchsia-500/40" />
                    </label>

                    <label className="block">
                      <span className="text-[9px] font-mono uppercase tracking-widest text-slate-600">Budget Tier</span>
                      <div className="grid grid-cols-3 gap-2 mt-1">
                        {([
                          { id: 'small',  label: 'Small',  hint: '~25k' },
                          { id: 'medium', label: 'Medium', hint: '~55k' },
                          { id: 'large',  label: 'Large',  hint: '150k+' },
                        ] as const).map(t => (
                          <button key={t.id} onClick={() => setProfile(p => ({...p, budgetTier: t.id}))}
                            className={`py-2 rounded-xl border text-[10px] font-bold transition-all ${
                              profile.budgetTier === t.id
                                ? 'bg-fuchsia-500/15 border-fuchsia-500/40 text-fuchsia-200'
                                : 'bg-white/[0.02] border-white/[0.08] text-slate-500 hover:text-white'
                            }`}>
                            <div>{t.label}</div>
                            <div className="text-[8px] font-mono opacity-70">LKR {t.hint}</div>
                          </button>
                        ))}
                      </div>
                    </label>

                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={() => setProfile(p => ({...p, hasDelivery: !p.hasDelivery}))}
                        className={`py-2 rounded-xl border text-[10px] font-bold transition-all ${
                          profile.hasDelivery ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-200' : 'bg-white/[0.02] border-white/[0.08] text-slate-500'
                        }`}>
                        {profile.hasDelivery ? '✓' : '○'} Home Delivery
                      </button>
                      <button onClick={() => setProfile(p => ({...p, hasWhatsApp: !p.hasWhatsApp}))}
                        className={`py-2 rounded-xl border text-[10px] font-bold transition-all ${
                          profile.hasWhatsApp ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-200' : 'bg-white/[0.02] border-white/[0.08] text-slate-500'
                        }`}>
                        {profile.hasWhatsApp ? '✓' : '○'} WhatsApp Biz
                      </button>
                    </div>

                    <label className="block">
                      <span className="text-[9px] font-mono uppercase tracking-widest text-slate-600">Business Goals</span>
                      <textarea value={profile.goals} onChange={e => setProfile(p => ({...p, goals: e.target.value}))}
                        placeholder="e.g. Grow WhatsApp list to 500 contacts, launch delivery"
                        rows={2}
                        className="w-full mt-1 bg-white/[0.03] border border-white/[0.08] rounded-xl px-3 py-2 text-[12px] text-white outline-none focus:border-fuchsia-500/40 resize-none" />
                    </label>

                    <button onClick={saveProfile}
                      className="w-full py-2.5 rounded-xl bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white font-black text-[11px] uppercase tracking-widest">
                      Save Profile
                    </button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
